import React, { useContext, useState } from "react";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Divider from "@mui/material/Divider";
import moment from "moment";
import { grey } from "@mui/material/colors";
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { OrderUserType } from "../../../constants/order";
import { getSellerPayoutBreakdown } from "../../utils/utils";
import { Item } from "../Item/Item";
import { orderStatuses, paymentStatuses, shippingStatuses } from "../../../constants/shipping";
import { OrderItemActions } from "../../../routes/Orders/Sales/OrderItemActions";
import { HistoryDialog } from "../HistoryDialog/HistoryDialog";
import { OrderPaymentMethodDisplay } from "../OrderPaymentMethodDisplay/OrderPaymentMethodDisplay";
import { ToasterContext } from "../Toaster/ToasterContext";
import { NotificationType } from "../../../constants/global";
import { CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions, Button, Select, MenuItem, FormControl, InputLabel, DialogContentText, RadioGroup, FormControlLabel, Radio, TextField, Checkbox, FormGroup } from "@mui/material";
import { useUpdateOrderStatuses } from "../../../queries/order/hooks/useUpdateOrderStatuses";
import { OrderTotalsSummary } from "../OrderTotalsSummary/OrderTotalsSummary";

interface HistoryGroupItemProps {
  orders: any;
  refetch: any;
  userType: OrderUserType.seller | OrderUserType.buyer;
  refetchLoading?: boolean;
  isAdmin?: boolean;
}

type StatusOption = 'DELIVERED' | 'CANCELED';
type UpdateType = 'STATUS' | 'TRACKING' | 'IS_CHARGED';

export const HistoryGroupItem: React.FunctionComponent<HistoryGroupItemProps> = ({
  orders,
  refetch,
  userType,
  refetchLoading = false,
  isAdmin = false,
}) => {
  const { addToast } = useContext(ToasterContext);
  const [open, setOpen] = useState<boolean>(false);
  const [selectedOrders, setSelectedOrders] = useState<any>(null);
  const [statusDialogOpen, setStatusDialogOpen] = useState<boolean>(false);
  const [confirmationDialogOpen, setConfirmationDialogOpen] = useState<boolean>(false);
  const [selectedOrderNumber, setSelectedOrderNumber] = useState<string>('');
  const [selectedStatus, setSelectedStatus] = useState<StatusOption>('DELIVERED');
  const [selectedOrder, setSelectedOrder] = useState<any>(null);
  const [updateType, setUpdateType] = useState<UpdateType>('STATUS');
  const [trackingNumber, setTrackingNumber] = useState<string>('');
  const [isCharged, setIsCharged] = useState<boolean | null>(null);
  const { updateStatuses, updateTracking, updateIsCharged } = useUpdateOrderStatuses();

  const handleStatusUpdate = async () => {
    try {
      if (updateType === 'STATUS') {
        let shippingStatus: number | null = null;
        let orderStatus: number | null = null;
        if (selectedStatus === 'DELIVERED') {
          shippingStatus = 4;
          orderStatus = null;
        } else if (selectedStatus === 'CANCELED') {
          shippingStatus = null;
          orderStatus = 0;
        }
        console.log('Updating order status:', {
          orderNumber: selectedOrderNumber,
          shippingStatus,
          orderStatus
        });
        await updateStatuses(selectedOrderNumber, shippingStatus, orderStatus, null, selectedOrder);
      } else if (updateType === 'TRACKING') {
        if (!trackingNumber.trim()) {
          addToast({ type: NotificationType.Error, title: 'Tracking number cannot be empty' });
          return;
        } else {
          // Update tracking number
          console.log('Updating tracking number:', {
            orderNumber: selectedOrderNumber,
            trackingNumber: trackingNumber.trim()
          });
          await updateTracking(selectedOrderNumber, trackingNumber.trim());
        }
      } else if (updateType === 'IS_CHARGED') {
        if (isCharged === null) {
          addToast({ type: NotificationType.Error, title: 'Please select a payment status' });
          return;
        } else {
          // Check if the new value is the same as existing value
          const currentIsCharged = selectedOrder?.isCharged;
          if (currentIsCharged === isCharged) {
            addToast({ 
              type: NotificationType.Info, 
              title: `Payment status is already set to ${isCharged ? 'Charged' : 'Not Charged'}` 
            });
            setStatusDialogOpen(false);
            setConfirmationDialogOpen(false);
            setIsCharged(null);
            return;
          }
          
          console.log('Updating isCharged:', {
            orderNumber: selectedOrderNumber,
            isCharged: isCharged
          });
          await updateIsCharged(selectedOrderNumber, isCharged);
        }
      }

      addToast({
        type: NotificationType.Success,
        title: updateType === 'STATUS'
          ? 'Order status updated successfully'
          : updateType === 'TRACKING'
          ? 'Tracking number updated successfully'
          : 'Payment status updated successfully'
      });
      refetch();
      setStatusDialogOpen(false);
      setConfirmationDialogOpen(false);
      setTrackingNumber('');
      setIsCharged(null);
    } catch (error: any) {
      addToast({ type: NotificationType.Error, title: error.message });
    }
  };

  const handleUpdateClick = () => {
    setUpdateType('STATUS');
    setTrackingNumber(selectedOrder?.trackingNumber || '');
    setStatusDialogOpen(true);
  };

  const handleStatusUpdateClick = () => {
    setConfirmationDialogOpen(true);
  };

  const handleCancelConfirmation = () => {
    setConfirmationDialogOpen(false);
    setStatusDialogOpen(false);
    setTrackingNumber('');
    setIsCharged(null);
  };

  const handleUpdateTypeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setUpdateType(event.target.value as UpdateType);
    if (event.target.value === 'TRACKING') {
      setTrackingNumber(selectedOrder?.trackingNumber || '');
    } else if (event.target.value === 'IS_CHARGED') {
      setIsCharged(null);
    }
  };

  const getTradeNetFee = (order: any) => {
    const { totalAmount, tradeNetFee } = order;
    return Number(totalAmount) * Number(tradeNetFee) / 100;
  };

  const getTotal = (orders: any) => {
    const filteredOrders = orders.filter((order: any) => order.status === 1);

    if (userType === OrderUserType.buyer) {
      const cutoffDateForGroundShipment = new Date("Mon Feb 20 2025 23:59:59 GMT+0400");
      let total = 0;
      filteredOrders.forEach((item: any) => {
        const orderDate = new Date(item.createdAt);
        const isNewOrderForGround = orderDate > cutoffDateForGroundShipment;
        const isFedexGround = item.ShippingMethod?.key === 'fedex-ground';
        const isShippingPaidBySeller = Boolean(item.isShippingPaidBySeller);

        const shippingFee =
          isShippingPaidBySeller || (isNewOrderForGround && isFedexGround)
            ? 0
            : Number(item.shippingFee) || 0;

        total += Number(item.totalAmount) + shippingFee;
      });

      return `${total.toFixed(2)}`;
    }

    const breakdown = getSellerPayoutBreakdown(filteredOrders);
    if (!breakdown) return "0.00";
    return breakdown.creditTotal.toFixed(2);
  };

  const isTheSameName = (items: any) => {
    let isTheSame = false;
    let name: string = items[0].split('_')[0];

    items.forEach((item: string) => {
      const nameItems = item.split('_');

      if (name !== nameItems[0]) {
        name = nameItems[0];
        isTheSame = false;
      } else {
        isTheSame = true;
      }
    });

    return isTheSame;
  };

  // Helper to strip "o_" prefix from orderNumber keys for display
  const stripOrderPrefix = (orderKey: string): string => {
    return orderKey.startsWith('o_') ? orderKey.slice(2) : orderKey;
  };

  const hasCanceledStatus = (orderKey: string) => {
    const allOrdersWithNumber = Object.values(orders[orderKey]).flat();
    return allOrdersWithNumber.some((order: any) => order.status === 0);
  };

  return (
    <Box>
      {isAdmin && <OrderTotalsSummary orders={orders} />}
      {Object.keys(orders).map((orderKey: any, key: number) => {
        const orderNumber = stripOrderPrefix(orderKey); // Strip "o_" prefix for display
        return (
          <Paper key={key} sx={{ boxShadow: 'none' }}>
            <Box key={orderNumber} sx={{ mt: 3, border: '2px solid #bdbdbd', borderRadius: '4px' }}>
              {Object.keys(orders[orderKey]).sort().map((seller: string, key: number) => {
                const currentOrder = orders[orderKey][seller][0];
              return (
                <Box key={seller}>
                  {key > 0 && <Divider />}
                  {(key === 0 || !isTheSameName(Object.keys(orders[orderKey]))) && (
                    <Box sx={{ display: 'flex', fontSize: 18, fontWeight: 600, p: 2, backgroundColor: grey[100] }}>
                      {isAdmin && currentOrder?.Seller?.legalName}
                    </Box>
                  )}
                  <Divider />
                  <Box
                    sx={{
                      p: 2,
                      cursor: 'pointer',
                      ':hover': { backgroundColor: grey[50] },
                    }}
                    onClick={() => {
                      setSelectedOrders(orders[orderKey][seller]);
                      setOpen(true);
                    }}
                  >
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: {
                          xs: '1fr',
                          sm: '1fr 1fr',
                          md: isAdmin ? 'repeat(7, minmax(0, 1fr))' : 'repeat(6, minmax(0, 1fr))',
                        },
                        columnGap: 2,
                        rowGap: 2,
                        alignItems: 'start',
                      }}
                    >
                      <Item label='Order Number' value={orderNumber} column>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          {orderNumber}
                          <ContentCopyIcon
                            onClick={(event: any) => {
                              event.stopPropagation();
                              navigator.clipboard.writeText(orderNumber);
                              addToast({ type: NotificationType.Success, title: 'Copied to Clipboard' });
                            }}
                            fontSize='small'
                            sx={{ ml: 1, cursor: 'pointer' }}
                          />
                        </Box>
                      </Item>
                      <Item label='Order Date' value={moment(currentOrder?.createdAt).format("MM/DD/YYYY")} column />
                      <Item label='Shipping Method' value={currentOrder?.ShippingMethod?.name || 'N/A'} column />
                      {isAdmin && (
                        <Item label='Payment Method' value='' column>
                          <OrderPaymentMethodDisplay order={currentOrder} />
                        </Item>
                      )}
                      <Item label='Status' value={shippingStatuses[currentOrder?.shippingStatus]?.label || 'N/A'} column>
                        {currentOrder?.status === 0 || currentOrder?.status === 2 ? (
                          <Box sx={{ width: 'fit-content', fontSize: '12px', borderRadius: '16px', color: orderStatuses[currentOrder?.status]?.color, backgroundColor: orderStatuses[currentOrder?.status]?.bgColor, display: 'flex', alignItems: 'center', justifyContent: 'center', pl: 2, pr: 2, pt: 0.5, pb: 0.5 }}>
                            {orderStatuses[currentOrder?.status]?.label || 'N/A'}
                          </Box>
                        ) : (
                          <>
                            {((currentOrder?.shippingStatus === 5 && currentOrder?.paymentStatus === 1) || currentOrder?.paymentStatus === 2) ? (
                              <Box sx={{ width: 'fit-content', textAlign: 'center', fontSize: '12px', borderRadius: '16px', color: paymentStatuses[currentOrder?.paymentStatus]?.color, backgroundColor: paymentStatuses[currentOrder?.paymentStatus]?.bgColor, display: 'flex', alignItems: 'center', justifyContent: 'center', pl: 2, pr: 2, pt: 0.5, pb: 0.5 }}>
                                {paymentStatuses[currentOrder?.paymentStatus]?.label || 'N/A'}
                              </Box>
                            ) : (
                              <Box sx={{ width: 'fit-content', textAlign: 'center', fontSize: '12px', borderRadius: '16px', color: shippingStatuses[currentOrder?.shippingStatus]?.color, backgroundColor: shippingStatuses[currentOrder?.shippingStatus]?.bgColor, display: 'flex', alignItems: 'center', justifyContent: 'center', pl: 2, pr: 2, pt: 0.5, pb: 0.5 }}>
                                {shippingStatuses[currentOrder?.shippingStatus]?.label || 'N/A'}
                              </Box>
                            )}
                          </>
                        )}
                      </Item>
                      <Item label='Tracking ID' value='' column>
                        {currentOrder?.status === 0 || currentOrder?.status === 2 ? '-' : (
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            {currentOrder?.trackingNumber || '-'}
                            {currentOrder?.trackingNumber && (
                              <ContentCopyIcon
                                onClick={(event: any) => {
                                  event.stopPropagation();
                                  navigator.clipboard.writeText(currentOrder?.trackingNumber);
                                  addToast({ type: NotificationType.Success, title: 'Copied to Clipboard' });
                                }}
                                fontSize='small'
                                sx={{ ml: 1, cursor: 'pointer' }}
                              />
                            )}
                          </Box>
                        )}
                      </Item>
                      {isAdmin ? (
                        <Item label="Total Amount" value={getTotal(orders[orderKey][seller])} hasAmount column />
                      ) : (
                        <Item label='Credit Total' value={getTotal(orders[orderKey][seller])} hasAmount column />
                      )}
                    </Box>
                    {isAdmin && currentOrder?.status !== 0 && currentOrder?.shippingStatus !== 0 && (
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={(e) => {
                          e.stopPropagation();
                          setSelectedOrderNumber(orderNumber);
                          handleUpdateClick();
                        }}
                        sx={{ mt: 2 }}
                      >
                        Update Status
                      </Button>
                    )}
                  </Box>
                  {currentOrder?.status === 1 && (
                    <OrderItemActions
                      orders={orders[orderKey][seller]}
                      key={orderNumber}
                      item={currentOrder}
                      user={userType}
                      refetch={refetch}
                      refetchLoading={refetchLoading}
                      isAdmin={isAdmin}
                    />
                  )}
                </Box>
              );
            })}
          </Box>
        </Paper>
        );
      })}
      {open && (
        <HistoryDialog
          orders={selectedOrders}
          setSelectedOrders={setSelectedOrders}
          open={open}
          setOpen={setOpen}
          user={userType}
        />
      )}

      <Dialog open={statusDialogOpen} onClose={() => setStatusDialogOpen(false)}>
        <DialogTitle>Update Order</DialogTitle>
        <DialogContent>
          <FormControl component="fieldset" sx={{ mt: 2, mb: 2 }}>
            <RadioGroup
              row
              value={updateType}
              onChange={handleUpdateTypeChange}
            >
              <FormControlLabel value="STATUS" control={<Radio />} label="Update Status" />
              <FormControlLabel value="TRACKING" control={<Radio />} label="Update Tracking Number" />
              <FormControlLabel value="IS_CHARGED" control={<Radio />} label="Update Payment Status" />
            </RadioGroup>
          </FormControl>

          {updateType === 'STATUS' ? (
            <FormControl fullWidth sx={{ mt: 2 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={selectedStatus}
                label="Status"
                onChange={(e) => {
                  setSelectedStatus(e.target.value as StatusOption);
                }}
              >
                <MenuItem value="DELIVERED">Delivered, Confirmation Pending</MenuItem>
                <MenuItem value="CANCELED">Canceled</MenuItem>
              </Select>
            </FormControl>
          ) : updateType === 'TRACKING' ? (
            <FormControl fullWidth sx={{ mt: 2 }}>
              <TextField
                type="text"
                value={trackingNumber}
                onChange={(e) => setTrackingNumber(e.target.value)}
                placeholder={selectedOrder?.trackingNumber ? selectedOrder.trackingNumber : "Enter tracking number"}
                fullWidth
                margin="normal"
                label="Tracking Number"
                helperText={selectedOrder?.trackingNumber ? "Current: " + selectedOrder.trackingNumber : "No tracking number set"}
              />
            </FormControl>
          ) : (
            <FormControl fullWidth sx={{ mt: 2 }}>
              <InputLabel>Payment Status</InputLabel>
              <Select
                value={isCharged === null ? '' : isCharged.toString()}
                label="Payment Status"
                onChange={(e) => {
                  const value = e.target.value;
                  setIsCharged(value === '' ? null : value === 'true');
                }}
              >
                <MenuItem value="true">Charged</MenuItem>
                <MenuItem value="false">Not Charged</MenuItem>
              </Select>
              <div style={{ marginTop: 8, fontSize: '0.75rem', color: '#666' }}>
                Current: {selectedOrder?.isCharged === true ? 'Charged' : selectedOrder?.isCharged === false ? 'Not Charged' : 'Unknown'}
              </div>
            </FormControl>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStatusDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleStatusUpdateClick}>
            Update
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={confirmationDialogOpen} onClose={handleCancelConfirmation}>
        <DialogTitle>Confirm Order Update</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {updateType === 'STATUS'
              ? 'Are you sure you want to update the Status?'
              : updateType === 'TRACKING'
              ? 'Are you sure you want to update the tracking number?'
              : 'Are you sure you want to update the payment status?'
            }
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelConfirmation}>No</Button>
          <Button onClick={handleStatusUpdate} variant="contained">
            Yes
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default HistoryGroupItem;